import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIncentiveOfferOperationDetail } from '../context/IncentiveOfferOperationDetailContext';
import { OperationPartyInfo } from '../types/IncentiveOfferOperationDetailContextType';

const getPartyInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

type PartySectionProps = {
  party: OperationPartyInfo | null;
  label: string;
  isBuyer: boolean;
  societyId: string;
};

const PartySection: FC<PartySectionProps> = ({
  party,
  label,
  isBuyer,
  societyId,
}) => {
  const { t } = useTranslate();
  const history = useHistory();

  const handleNavigateToPartner = () => {
    if (party?.beneficiaryId && societyId) {
      history.push(`/detalle-beneficiario/${societyId}/${party.beneficiaryId}`);
    }
  };

  return (
    <div className="col-12 col-sm-6">
      <h6 className="overline-title text-soft mb-2">{label}</h6>
      {party ? (
        <>
          <div
            className="trading-offer-party-card-item d-flex align-items-center cursor-pointer mb-2"
            onClick={handleNavigateToPartner}
          >
            <div
              className={`user-avatar user-avatar-sm hover-shadow ${
                isBuyer ? 'bg-info-dim' : 'bg-primary-dim'
              } mr-2`}
            >
              <span>{getPartyInitials(party.name)}</span>
            </div>
            <div className="min-w-0 flex-grow-1 overflow-hidden">
              <h6 className="party-name mb-0 text-ellipsis">{party.name}</h6>
              <span className="party-number text-soft fs-13px">
                {t('IncentivesMarketBeneficiaryNumber')}: {party.partnerNumber || '-'}
              </span>
            </div>
          </div>

          <div className="fs-13px">
            <div className="d-flex justify-content-between py-1">
              <span className="text-soft">{t('IncentivesMarketCurrentUnits')}:</span>
              <strong>{formatNumber(party.sharesBefore)}</strong>
            </div>

            <div className="d-flex justify-content-between py-1">
              <span className="text-soft">{t('AfterOperation')}:</span>
              <strong className={isBuyer ? 'text-info' : 'text-danger'}>
                {formatNumber(party.sharesAfter)}
              </strong>
            </div>

            <div className="d-flex justify-content-between py-1">
              <span className="text-soft">{t('CapitalAfterOperation')}:</span>
              <strong className={isBuyer ? 'text-info' : 'text-danger'}>
                {party.capitalPercentageAfter.toFixed(2)}%
              </strong>
            </div>
          </div>
        </>
      ) : (
        <p className="text-soft fs-13px">{t('NoInfoAvailable')}</p>
      )}
    </div>
  );
};

type IncentiveOfferOperationDetailPartiesCardProps = {
  buyerInfo: OperationPartyInfo | null;
  sellerInfo: OperationPartyInfo | null;
};

const IncentiveOfferOperationDetailPartiesCard: FC<
  IncentiveOfferOperationDetailPartiesCardProps
> = ({ buyerInfo, sellerInfo }) => {
  const { t } = useTranslate();
  const { data } = useIncentiveOfferOperationDetail();

  const buyerLabel =
    !data.isAdmin && data.userDocumentRole === 'buyer'
      ? t('BuyerYou')
      : t('Buyer');

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <h6 className="card-title mb-3">
          <em className="icon ni ni-users text-primary mr-2" />
          {t('OperationParties')}
        </h6>
        <div className="row g-4">
          <PartySection
            isBuyer
            label={buyerLabel}
            party={buyerInfo}
            societyId={data.societyId}
          />
          <PartySection
            isBuyer={false}
            label={t('Seller')}
            party={sellerInfo}
            societyId={data.societyId}
          />
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferOperationDetailPartiesCard;
